import { CategoryApi } from "@/services/collection/categories";
import { ItemStatusUrlString } from "@/services/collection";
import { Layout } from "@/router/types";
import { handleItemEditationAccess } from "@/router/guards/handleItemEditationAccess";
import { type RouteRecordRaw } from "vue-router";
import { RouteName } from "@/router/routeNameEnum";
import { gettext } from "@/services/translation";
import { factoryLandingPageSetupBoardGames, factoryLandingPageSetupLegoSets, factoryLandingPageSetupSportsCards, factoryLandingPageSetupVinyls } from "@/services/landingPages";
import { saveLastCategoryId } from "@/services/aiHelpers";
import { createAiLookupFormLink, createError404Link, createMarketplaceLink, createPurchasesLink } from "@/router/linkFactories";

export const routes: RouteRecordRaw[] = [
  {
    name: RouteName.ArticleDraft,
    path: "magazine/preview/:id(\\d+)-:url/",
    components: { mainContent: () => import("@/views/magazine/ArticleDetail.vue") },
  },
  {
    path: `portfolio/:itemStatus(${ItemStatusUrlString.Archived}|${ItemStatusUrlString.InMyCollection}|${ItemStatusUrlString.OnMarketplace})?`,
    name: RouteName.Portfolio,
    props: true,
    components: { mainContent: () => import("@/views/Portfolio.vue") },
    meta: {
      breadcrumbs: [{ text: () => gettext.$gettext("Portfolio") }],
      grayBackground: true,
    },
  },
  {
    name: RouteName.MyItemDetail,
    path: "my-item/:id(\\d+)/:nameSeoSlug?",
    components: { mainContent: () => import("@/views/ItemEdit.vue") },
    meta: {
      public: true, // Only to allow beforeEnter() execution even for not logged in users.
      grayBackground: true,
    },
    beforeEnter: handleItemEditationAccess,
  },
  {
    name: RouteName.Dashboard,
    path: "dashboard",
    components: { mainContent: () => import("@/views/Dashboard.vue") },
  },
  {
    path: "account",
    children: [
      {
        path: "settings",
        name: RouteName.AccountSettings,
        components: { mainContent: () => import("@/views/account/Settings.vue") },
        meta: {
          breadcrumbs: [
            { text: () => gettext.$gettext("Moje Colectio") }, //
            { text: () => gettext.$gettext("Moje nastavení") },
          ],
          grayBackground: true,
        },
      },
      {
        path: "year-in-review",
        name: RouteName.YearInReview,
        components: { mainContent: () => import("@/views/account/YearInReview.vue") },
        meta: {
          breadcrumbs: [{ text: () => gettext.$gettext("Moje Colectio") }, { text: () => gettext.$gettext("Rok v přehledu") }],
          grayBackground: true,
        },
      },
    ],
  },
  {
    path: "saved-items",
    name: RouteName.SavedItems,
    components: { mainContent: () => import("@/views/SavedItems.vue") },
    meta: {
      breadcrumbs: [{ text: () => gettext.$gettext("Oblíbené předměty") }],
    },
  },
  {
    path: "messages/email-unsubscription-successfull",
    name: RouteName.ChatEmailNotificationsUnsubscribed,
    components: { mainContent: () => import("@/views/chat/NotificationsUnsubscribed.vue") },
    meta: {
      public: true,
      breadcrumbs: [
        {
          to: { name: RouteName.Chat },
          text: () => gettext.$gettext("Zprávy"),
        },
        {
          text: () => gettext.$gettext("E-mailové notifikace zrušeny"),
        },
      ],
    },
  },
  {
    path: "messages/email-unsubscription-failed",
    name: RouteName.ChatEmailNotificationsUnsubscribeError,
    components: { mainContent: () => import("@/views/chat/NotificationsUnsubscribeError.vue") },
    meta: {
      public: true,
      breadcrumbs: [
        {
          to: { name: RouteName.Chat },
          text: () => gettext.$gettext("Zprávy"),
        },
        {
          text: () => gettext.$gettext("E-mailové notifikace se nepodařilo zrušit"),
        },
      ],
    },
  },
  {
    path: "messages/:buddyId(\\d+)?/:buddyNickname?",
    name: RouteName.Chat,
    meta: {
      breadcrumbs: [{ text: () => gettext.$gettext("Zprávy") }],
    },
    components: { mainContent: () => import("@/views/chat/Chat.vue") },
  },
  {
    path: "colectio-ai",
    children: [
      {
        path: "history",
        name: RouteName.AiHistory,
        components: { mainContent: () => import("@/views/ai/History.vue") },
        meta: {
          grayBackground: true,
          breadcrumbs: [
            {
              text: () => gettext.$gettext("AI identifikace"),
              to: createAiLookupFormLink(),
            },
            {
              text: () => gettext.$gettext("Historie"),
            },
          ],
        },
      },
    ],
  },
  {
    name: RouteName.Notifications,
    path: "notifications",
    components: { mainContent: () => import("@/views/NotificationList.vue") },
    meta: {
      breadcrumbs: [{ text: () => gettext.$gettext("Notifikace") }],
    },
  },
  {
    name: RouteName.Error404,
    path: "/:countryCode(cz|sk)/:pathMatch(.*)",
    components: { mainContent: () => import("@/views/error/NotFound404.vue") },
    meta: {
      public: true,
    },
  },
  {
    path: "login",
    components: { mainContent: () => import("@/views/auth/Login.vue") },
    name: RouteName.Login,
    meta: {
      public: true,
      layout: Layout.LockingState,
      redirectWhenLoggedIn: { name: RouteName.Dashboard },
    },
  },
  {
    path: "sign-in",
    children: [
      {
        path: "",
        name: RouteName.Registration,
        components: { mainContent: () => import("@/views/auth/Registration.vue") },
        meta: {
          public: true,
          layout: Layout.LockingState,
          redirectWhenLoggedIn: { name: RouteName.Dashboard },
        },
      },
      {
        name: RouteName.RegistrationEmailVerified,
        path: "email-verification/:hash",
        components: { mainContent: () => import("@/views/auth/EmailVerified.vue") },
        meta: {
          public: true,
          layout: Layout.LockingState,
        },
      },
      {
        name: RouteName.AccountSetup,
        path: "completion",
        components: { mainContent: () => import("@/views/auth/AccountSetup.vue") },
        meta: {
          layout: Layout.LockingState,
        },
      },
    ],
  },
  {
    path: "password-reset",
    children: [
      {
        path: "",
        name: RouteName.PasswordRestore,
        components: { mainContent: () => import("@/views/auth/PasswordRestoreRequest.vue") },
        meta: {
          public: true,
          layout: Layout.LockingState,
          redirectWhenLoggedIn: { name: RouteName.Dashboard },
        },
      },
    ],
  },
  {
    path: "password-reset/:hash",
    name: RouteName.PasswordRestoreProcessing,
    components: { mainContent: () => import("@/views/auth/PasswordRestore.vue") },
    meta: {
      public: true,
      layout: Layout.LockingState,
      redirectWhenLoggedIn: { name: RouteName.Dashboard },
    },
  },
  {
    name: RouteName.Homepage,
    path: "",
    components: { mainContent: () => import("@/views/Homepage.vue") },
    meta: {
      grayBackground: true,
      public: true,
      redirectWhenLoggedIn: { name: RouteName.Dashboard },
    },
  },
  {
    name: RouteName.LandingPageCategory,
    path: ":landingPageCategory(lego-sets|vinyl-records|board-games|sports-cards)",
    components: { mainContent: () => import("@/views/LandingPage.vue") },
    meta: {
      public: true,
      landingPageDataFactory: factoryLandingPageSetupLegoSets,
    },
    beforeEnter: (to, _, next) => {
      let dataFactory;
      const category = to.params.landingPageCategory;
      if (category === "lego-sets") {
        dataFactory = factoryLandingPageSetupLegoSets;
      } else if (category === "vinyl-records") {
        dataFactory = factoryLandingPageSetupVinyls;
      } else if (category === "board-games") {
        dataFactory = factoryLandingPageSetupBoardGames;
      } else if (category === "sports-cards") {
        dataFactory = factoryLandingPageSetupSportsCards;
      } else {
        throw new Error("DEV: Should not happen.");
      }
      to.meta.landingPageDataFactory = dataFactory;
      next();
    },
  },
  {
    name: RouteName.Gdpr,
    path: "privacy-policy",
    components: { mainContent: () => import("@/views/legal/PrivacyPolicy.vue") },
    meta: {
      public: true,
      breadcrumbs: [{ text: () => gettext.$gettext("Zásady ochrany osobních údajů") }],
    },
  },
  {
    name: RouteName.Tac,
    path: "terms-and-conditions",
    components: { mainContent: () => import("@/views/legal/TermsAndConditions.vue") },
    meta: {
      public: true,
      breadcrumbs: [{ text: () => gettext.$gettext("Všeobecné obchodní podmínky") }],
    },
  },
  {
    name: RouteName.Guide,
    path: "guide",
    components: { mainContent: () => import("@/views/Guide.vue") },
    meta: {
      public: true,
      breadcrumbs: [{ text: () => gettext.$gettext("Právní informace") }],
    },
  },
  {
    name: RouteName.Contacts,
    path: "contacts",
    components: { mainContent: () => import("@/views/Contacts.vue") },
    meta: {
      public: true,
      breadcrumbs: [{ text: () => gettext.$gettext("Kontakty") }],
    },
  },
  {
    name: RouteName.About,
    path: "about-us",
    components: { mainContent: () => import("@/views/AboutUs.vue") },
    meta: {
      public: true,
      breadcrumbs: [{ text: () => gettext.$gettext("O nás") }],
    },
  },
  {
    path: "magazine",
    children: [
      {
        name: RouteName.Article,
        path: ":id(\\d+)-:url",
        components: { mainContent: () => import("@/views/magazine/ArticleDetail.vue") },
        meta: {
          public: true,
        },
      },
      {
        name: RouteName.Magazine,
        path: ":category?",
        components: { mainContent: () => import("@/views/magazine/ArticleList.vue") },
        meta: {
          public: true,
          breadcrumbs: [{ text: () => gettext.$gettext("Magazín") }],
        },
      },
    ],
  },
  {
    name: RouteName.PublicItemDetail,
    path: "collector/:nickname/:slug/:itemId",
    components: { mainContent: () => import("@/views/PublicCollectionItem.vue") },
    meta: {
      public: true,
      grayBackground: true,
    },
  },
  {
    name: RouteName.Collector,
    path: "collector/:nickname",
    components: { mainContent: () => import("@/views/Collector.vue") },
    meta: {
      public: true,
      grayBackground: true,
    },
  },
  {
    path: "marketplace",
    children: [
      {
        path: "",
        name: RouteName.Marketplace,
        components: { mainContent: () => import("@/views/marketplace/index.vue") },
        meta: {
          public: true,
          grayBackground: true,
          breadcrumbs: [{ to: createMarketplaceLink(), text: () => gettext.$gettext("Tržiště") }],
          preventScrollToTop: true,
        },
      },
      {
        name: RouteName.MarketplaceItemCreated,
        path: "item-created/:itemId(\\d+)",
        components: { mainContent: () => import("@/views/marketplace/ItemCreated.vue") },
        meta: {
          grayBackground: true,
        },
      },
      {
        name: RouteName.ShoppingCart,
        path: "cart",
        components: { mainContent: () => import("@/views/marketplace/Cart.vue") },
        meta: {
          renderSimpleFooter: true,
        },
      },
      {
        path: "order",
        children: [
          {
            name: RouteName.MarketplaceOrderSuccess,
            path: "thank-you",
            components: { mainContent: () => import("@/views/marketplace/order/ThankYou.vue") },
            meta: {
              grayBackground: true,
            },
          },
          {
            name: RouteName.MarketplaceOrder,
            path: "seller/:sellerNickname",
            components: { mainContent: () => import("@/views/marketplace/order/index.vue") },
            meta: {
              renderSimpleFooter: true,
            },
          },
        ],
      },
      {
        name: RouteName.MarketplaceItem,
        path: "item/:itemId(\\d+)/:seoSlug",
        components: { mainContent: () => import("@/views/marketplace/Item.vue") },
        meta: {
          public: true,
          grayBackground: true,
        },
      },
    ],
  },
  {
    path: "sales",
    children: [
      {
        path: "",
        name: RouteName.Sales,
        components: { mainContent: () => import("@/views/Sales.vue") },
        meta: {
          breadcrumbs: [
            { text: () => gettext.$gettext("Moje Colectio") }, //
            { text: () => gettext.$gettext("Moje prodeje") },
          ],
          grayBackground: true,
        },
      },
      {
        path: ":orderId(\\d+)",
        name: RouteName.Sale,
        components: { mainContent: () => import("@/views/Sale.vue") },
        meta: {
          grayBackground: true,
        },
      },
    ],
  },
  {
    path: "purchases",
    children: [
      {
        path: "",
        name: RouteName.Purchases,
        components: { mainContent: () => import("@/views/Purchases.vue") },
        meta: {
          breadcrumbs: [
            { text: () => gettext.$gettext("Moje Colectio") }, //
            { text: () => gettext.$gettext("Moje nákupy"), to: createPurchasesLink() },
          ],
          grayBackground: true,
        },
      },
      {
        path: ":orderId(\\d+)",
        name: RouteName.Purchase,
        components: { mainContent: () => import("@/views/Purchase.vue") },
        meta: {
          grayBackground: true,
        },
      },
    ],
  },
  {
    name: RouteName.Collectors,
    path: "collectors",
    components: { mainContent: () => import("@/views/Collectors.vue") },
    meta: {
      public: true,
      grayBackground: true,
      breadcrumbs: [{ text: () => gettext.$gettext("Sběratelé") }],
    },
  },
  {
    path: "database",
    children: [
      {
        path: "",
        name: RouteName.Catalogue,
        components: { mainContent: () => import("@/views/collectibles/Intro.vue") },
        meta: {
          public: true,
          grayBackground: true,
          breadcrumbs: [{ text: () => gettext.$gettext("Databáze") }],
        },
      },
      {
        name: RouteName.CatalogueCategory,
        path: ":categoryUrl",
        components: { mainContent: () => import("@/views/collectibles/CollectibleList.vue") },
        meta: {
          public: true,
          grayBackground: true,
        },
      },
      {
        name: RouteName.CatalogueItem,
        path: ":categoryUrl/:itemId(\\d+)",
        components: { mainContent: () => import("@/views/collectibles/Collectible.vue") },
        meta: {
          public: true,
          grayBackground: true,
        },
      },
    ],
  },
  {
    path: ":originalUrl(ai-vision):catchRest(.*)",
    redirect: (to) => {
      return {
        path: `${to.params.countryCode}/ai-identification${to.params.catchRest || ""}`,
        query: to.query,
        hash: to.hash,
      };
    },
  },
  {
    path: "ai-identification",
    children: [
      {
        path: "",
        name: RouteName.AiForm,
        components: { mainContent: () => import("@/views/ai/Form") },
        meta: {
          public: true,
          grayBackground: true,
        },
      },
      {
        path: "category/:seoSlug",
        name: RouteName.AiFormWithPreselectedCategory,
        beforeEnter: async (to, from, next) => {
          const slug = String(to.params.seoSlug);
          const category = await CategoryApi.fetchByUrl(slug);
          if (category === undefined) {
            return next(createError404Link());
          } else {
            await saveLastCategoryId(category.id);
            return next(createAiLookupFormLink());
          }
        },
        meta: {
          public: true,
        },
        components: { mainContent: () => import("@/views/ai/Form") }, // Should not be ever used (beforeEnter always redirects)
      },
      {
        path: "history/:itemId",
        name: RouteName.AiDetail,
        components: { mainContent: () => import("@/views/ai/Detail.vue") },
        meta: {
          public: true,
          grayBackground: true,
        },
      },
    ],
  },
  {
    path: "colectio-ai",
    children: [
      {
        path: "",
        name: RouteName.AiLandingPage,
        components: { mainContent: () => import("@/views/ai/AiLanding.vue") },
        meta: {
          public: true,
        },
      },
      {
        path: "category/:categorySeoSlug",
        name: RouteName.AiLandingPageWithCategory,
        components: { mainContent: () => import("@/views/ai/AiLanding.vue") },
        meta: {
          public: true,
        },
      },
    ],
  },
  {
    path: "soutez",
    children: [
      {
        path: "",
        name: RouteName.ComicCon,
        components: { mainContent: () => import("@/views/ComicCon.vue") },
        meta: {
          public: true,
          hideFooter: true,
        },
      },
      {
        path: "pravidla",
        name: RouteName.ComicConTerms,
        components: { mainContent: () => import("@/views/legal/ComicConTerms.vue") },
        meta: {
          public: true,
        },
      },
    ],
  },
  {
    path: "support",
    children: [
      {
        path: "",
        name: RouteName.Support,
        components: { mainContent: () => import("@/views/support/index.vue") },
        meta: {
          public: true,
          breadcrumbs: [{ text: () => gettext.$gettext("Centrum nápovědy") }],
          grayBackground: true,
        },
      },
    ],
  },
];
